/*!

=========================================================
* Argon Dashboard React - v1.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2019 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from 'react';

// reactstrap components
import {
  Spinner,
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  Alert,
} from 'reactstrap';
// core components
import utils from '../utils.js';

class AmCredentials extends React.Component {
  constructor(props) {
    super(props);
    this.formOnChange = this.formOnChange.bind(this);
    this.state = this.getDefaultFormState();
  }

  getDefaultFormState() {
    return {
      isFilledOut: null,
      vaStatus: null,
      email: null,
      password: null,
      touched: false,
      saving: false,
      errorMessage: null,
      successMessage: null,

      // Test order
      testOrderUrl: null,
      testOrderTouched: false,
      testOrderErrorMessage: null,
      testOrderSuccessMessage: null,
      testOrderSaving: false,
    };
  }

  async componentDidMount() {
    if (!utils.isPC()) {
      this.setState({
        saving: false,
        errorMessage:
          'Please use a non-mobile device to update your credentials. Use the computer you usually browse Walmart with.',
      });
      return;
    }
    if (!this.props.credentialsLoading) {
      await this.updateState();
    }
  }

  async componentDidUpdate(prevProps) {
    if (this.props.didSelectedCredChange(this.props, prevProps)) {
      await this.updateState();
    }
  }

  async updateState() {
    const acc = this.props.selectedAccount;
    if (!acc) {
      return this.setState({
        ...this.getDefaultFormState(),
        errorMessage:
          'Fill out all fields so the bot can perform autonomously. Your data is ONLY used for checking out on your behalf.',
      });
    }

    const { isFilledOut, email, vaStatus } = acc;
    this.setState({
      ...this.getDefaultFormState(),
      vaStatus,
      email,
      isFilledOut,
      errorMessage: isFilledOut
        ? null
        : 'Fill out all fields so the bot can perform autonomously. Your data is ONLY used for checking out on your behalf.',
    });
  }

  handleSave = async () => {
    this.setState({
      saving: true,
      errorMessage: null,
      successMessage: null,
    });

    const { email, password, vaStatus } = this.state;

    if (!utils.isPC()) {
      this.setState({
        saving: false,
        errorMessage:
          'Error: Please use a non-mobile device to update your credentials. Use the computer you usually browse Walmart with.',
      });
      return;
    }

    let vendorAccountId = null;
    let vaType = undefined;

    if (this.props.selectedAccount) {
      vendorAccountId = this.props.selectedAccount.vendorAccountId;
    }
    if (this.props.selectedCred) {
      vaType = this.props.selectedCred.vaType;
    }

    const { account, errorMessage } = await utils.putCredentials({
      vendor: 'walmart',
      vaStatus,
      vaType,
      vendorAccountId,
      email,
      password,
      userAgent: utils.getUserAgent(),
    });
    if (account) {
      const { isFilledOut, vendorAccountId: returnedVendorAccountId } = account;
      this.setState({
        touched: false,
        saving: false,
        isFilledOut,
        successMessage:
          'Credentials saved. If you inputted new information, you should receive a Discord webhook notification once the bot logs into your account (it can take up to an hour).',
      });
      await this.props.updateCredentials({
        vaIdToSelectAfter: returnedVendorAccountId,
      });
    } else {
      this.setState({
        errorMessage,
        saving: false,
      });
    }
  };

  renderSaveButton = () => {
    const color = this.state.touched ? 'warning' : 'primary';
    const spinner = <Spinner className="ml-1" size="sm" color="warning" />;
    const saveButton = (
      <Button
        disabled={!this.state.touched}
        color={color}
        size="lg"
        type="button"
        className="ml-3"
        onClick={this.handleSave}
      >
        Save Changes
      </Button>
    );
    return (
      <>
        {this.props.credentialsLoading ? null : saveButton}
        {this.state.saving ? spinner : null}
      </>
    );
  };

  async formOnChange(e) {
    const inputName = e.target.name;
    const inputValue = e.target.value;
    this.setState({
      [inputName]: inputValue,
      touched: true,
    });
  }

  testOrderFormOnChange = (e) => {
    const inputName = e.target.name;
    const inputValue = e.target.value;
    this.setState({
      [inputName]: inputValue,
      testOrderTouched: true,
    });
  };

  renderErrors() {
    if (this.state.errorMessage) {
      return <Alert color="danger">{this.state.errorMessage}</Alert>;
    }
    if (this.state.successMessage) {
      return <Alert color="success">{this.state.successMessage}</Alert>;
    }
  }

  handleCheckboxChange() {
    const newStatus =
      this.state.vaStatus === 'active' ? 'user_disabled' : 'active';
    this.setState({
      vaStatus: newStatus,
      touched: true,
    });
  }

  renderEnabledCheckbox() {
    return this.state.vaStatus ? (
      <Row>
        <Col lg="12">
          <div className="custom-control custom-checkbox mb-4">
            <input
              className="custom-control-input"
              checked={this.state.vaStatus === 'active'}
              id="customCheck2"
              type="checkbox"
              onChange={() => this.handleCheckboxChange()}
            />
            <label className="custom-control-label" htmlFor="customCheck2">
              Enabled
            </label>
          </div>
        </Col>
      </Row>
    ) : null;
  }

  renderTestOrder() {
    return (
      <Form>
        <hr className="mt-5" />
        {this.state.testOrderErrorMessage ? (
          <Alert color="danger">{this.state.testOrderErrorMessage}</Alert>
        ) : null}
        {this.state.testOrderSuccessMessage ? (
          <Alert color="success">{this.state.testOrderSuccessMessage}</Alert>
        ) : null}
        <h5 className="mb-4">
          <div className="heading-small text-muted">Test Order Request</div>
          <div>
            Use this to verify the bot can successfully make purchases with your
            account. Note that test orders are delayed and not indicative of the
            bot's performance. Provide the URL to the Walmart product page of
            the item you want to purchase.
          </div>
          <b>Note: Product must be in stock.</b>
        </h5>
        <div className="pl-lg-4">
          <Row>
            <Col lg="12">
              <FormGroup>
                <label
                  className="form-control-label"
                  htmlFor="input-testOrderLink"
                >
                  Walmart Product Page URL
                </label>
                <Input
                  name="testOrderUrl"
                  value={this.state.testOrderUrl || ''}
                  maxLength="2000"
                  className="form-control-alternative"
                  id="input-testOrderUrl"
                  placeholder="https://www.walmart.com/ip/Some-Product/1234567"
                  type="text"
                  onChange={this.testOrderFormOnChange}
                />
              </FormGroup>
              <Row>{this.renderTestOrderBtn()}</Row>
            </Col>
          </Row>
        </div>
      </Form>
    );
  }

  renderTestOrderBtn = () => {
    const color = this.state.testOrderTouched ? 'warning' : 'primary';
    const spinner = <Spinner className="ml-1" size="sm" color="warning" />;
    const saveButton = (
      <Button
        disabled={!this.state.testOrderTouched || this.state.testOrderSaving}
        color={color}
        size="lg"
        type="button"
        className="ml-3"
        onClick={this.handleTestOrderSubmit}
      >
        Request Test Order
      </Button>
    );
    return (
      <>
        {this.props.credentialsLoading ? null : saveButton}
        {this.state.testOrderSaving ? spinner : null}
      </>
    );
  };

  handleTestOrderSubmit = async () => {
    const selectedAccount = this.props.selectedAccount;
    if (!selectedAccount) return;
    this.setState({
      testOrderSaving: true,
      testOrderErrorMessage: null,
      testOrderSuccessMessage: null,
    });

    const url = this.state.testOrderUrl;

    const { errorMessage } = await utils.submitTestOrder({
      vendor: 'walmart',
      url,
      vendorAccountId: selectedAccount.vendorAccountId,
    });
    if (errorMessage) {
      this.setState({
        testOrderSaving: false,
        testOrderErrorMessage:
          'Error submitting request. Make sure URL is in format https://www.walmart.com/ip/Some-Product/1234567',
        testOrderTouched: false,
      });
    } else {
      this.setState({
        testOrderSaving: false,
        testOrderSuccessMessage:
          'Request submitted. Check your webhook notifications for updates.',
        testOrderTouched: false,
      });
    }
  };

  render() {
    if (!this.props.selectedCred) return 'No selected instance.';
    const vendor = this.props.selectedCred.vendor;
    return (
      <>
        {/*<UserHeader />*/}
        {/* Page content */}
        <Container className="mt--7" fluid>
          <Row>
            <Col className="order-xl-1" xl="8">
              <Card className="bg-secondary shadow">
                <CardHeader className="bg-white border-0">
                  <Row className="align-items-center">
                    <Col xs="8">
                      <h3 className="mb-0">
                        {`${utils.titleCase(vendor)} Credentials`}
                        {this.props.credentialsLoading ? (
                          <Spinner
                            className="ml-2 mb-1"
                            size="sm"
                            color="warning"
                          />
                        ) : null}
                      </h3>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody>
                  <Alert color="default">
                    Hold up! Make sure to read the{' '}
                    <a href="https://docs.snailbot.io/walmart" target="_blank">
                      Walmart Setup Guide
                    </a>{' '}
                    before entering your credentials!
                  </Alert>
                  {this.renderErrors()}
                  <Form>
                    <h5 className="mb-4">
                      <div className="heading-small text-muted">
                        Walmart Login Info
                      </div>
                      <div>
                        Your Walmart login info is required for the bot to make
                        purchases on your behalf. Your info is encrypted using
                        the latest security standards.
                      </div>
                      <div>
                        Read this carefully:{' '}
                        <a
                          href="https://docs.snailbot.io/walmart"
                          target="_blank"
                        >
                          Walmart Setup Guide
                        </a>
                      </div>
                    </h5>
                    <div className="pl-lg-4">
                      <Row>
                        <Col lg="6">
                          <FormGroup>
                            <label
                              className="form-control-label"
                              htmlFor="input-email"
                            >
                              Email
                            </label>
                            <Input
                              name="email"
                              maxLength="100"
                              value={this.state.email || ''}
                              className="form-control-alternative"
                              id="input-email"
                              placeholder="snail@email.com"
                              type="text"
                              onChange={this.formOnChange}
                            />
                          </FormGroup>
                        </Col>
                        <Col lg="6">
                          <FormGroup>
                            <label
                              className="form-control-label"
                              htmlFor="input-password"
                            >
                              Password
                            </label>
                            <Input
                              name="password"
                              autoComplete="off"
                              maxLength="100"
                              value={this.state.password || ''}
                              className="form-control-alternative"
                              // value="lucky.jesse"
                              id="input-password"
                              placeholder={
                                this.state.isFilledOut
                                  ? '<HIDDEN>'
                                  : 'ballsack123'
                              }
                              type="text"
                              onChange={this.formOnChange}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col lg="12">
                          {this.renderEnabledCheckbox()}
                          <Row>{this.renderSaveButton()}</Row>
                        </Col>
                      </Row>
                    </div>
                  </Form>
                  {this.renderTestOrder()}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }
}

export default AmCredentials;
